import JSZip from "jszip";
import type { VillageSatelliteImage } from "../../domain/contracts";
import { fetchVillageSatelliteBlobByKey } from "../publicApi";

type DownloadEntry = {
  name: string;
  blob: Blob;
};

const mimeExtensions: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/tiff": "tif",
};

function sanitizeFilename(value: string): string {
  return value.replace(/[<>:"/\\|?*]+/g, "_").trim();
}

function extensionFromMime(mimeType: string | null | undefined): string {
  if (!mimeType) {
    return "jpg";
  }
  return mimeExtensions[mimeType.toLowerCase()] ?? "jpg";
}

function fallbackNameFromKey(key: string): string {
  const token = key.split("/").pop() || "satellite-image";
  return sanitizeFilename(token);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function buildZipName(villageName: string | null): string {
  const normalized = (villageName ?? "village")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const base = normalized || "village";
  return `${base}-sat-images.zip`;
}

function buildImageFilename(item: VillageSatelliteImage): string {
  const year = Number.isFinite(item.imageYear) ? Math.trunc(item.imageYear) : 0;
  const index = Number.isFinite(item.imageIndex) ? Math.trunc(item.imageIndex) : 0;
  const ext = extensionFromMime(item.mimeType);
  const safeType = item.imageType === "other" ? "other" : "satellite";

  if (year > 0) {
    return `${year}-${safeType}-${String(index + 1).padStart(2, "0")}.${ext}`;
  }

  return `${safeType}-${String(index + 1).padStart(2, "0")}.${ext}`;
}

function uniquifyFilename(filename: string, occurrence: number): string {
  if (occurrence <= 1) {
    return filename;
  }

  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0) {
    return `${filename}-${occurrence}`;
  }

  const base = filename.slice(0, lastDot);
  const ext = filename.slice(lastDot);
  return `${base}-${occurrence}${ext}`;
}

async function buildEntries(items: VillageSatelliteImage[]): Promise<DownloadEntry[]> {
  const entries: DownloadEntry[] = [];
  const filenameCounts = new Map<string, number>();

  for (const item of items) {
    if (!item.r2Key) {
      continue;
    }

    const blob = await fetchVillageSatelliteBlobByKey(item.r2Key, item.mimeType);
    const rawFilename = item.imageYear > 0
      ? buildImageFilename(item)
      : fallbackNameFromKey(item.r2Key);
    const nextCount = (filenameCounts.get(rawFilename) ?? 0) + 1;
    filenameCounts.set(rawFilename, nextCount);
    const filename = uniquifyFilename(rawFilename, nextCount);

    entries.push({
      name: filename,
      blob,
    });
  }

  return entries;
}

export async function downloadVillageSatelliteBundle(items: VillageSatelliteImage[], villageName: string | null): Promise<void> {
  if (items.length === 0) {
    return;
  }

  const sorted = [...items].sort((left, right) => {
    if (left.imageYear !== right.imageYear) {
      return left.imageYear - right.imageYear;
    }
    return left.imageIndex - right.imageIndex;
  });

  const entries = await buildEntries(sorted);
  if (entries.length === 0) {
    return;
  }

  const zip = new JSZip();
  for (const entry of entries) {
    zip.file(entry.name, entry.blob);
  }

  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, sanitizeFilename(buildZipName(villageName)));
}
